import axios from 'axios';
import { API_VERSION_PREFIX } from './routes';
import { apiRoutes } from './routes';
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from './sessionTokens';
import { apiRateLimitCooldown, rateLimitPartition } from './rateLimitCooldown';

declare module 'axios' {
  interface AxiosRequestConfig {
    helixAuth?: 'portal' | 'connector';
  }

  interface InternalAxiosRequestConfig {
    helixAuth?: 'portal' | 'connector';
  }
}

export interface ApiError {
  code: string;
  message: string;
  status: number;
  correlationId?: string;
  retryable: boolean;
  retryAfterMs?: number;
}

export class NormalizedApiError extends Error implements ApiError {
  readonly code: string;
  readonly status: number;
  readonly correlationId?: string;
  readonly retryable: boolean;
  readonly retryAfterMs?: number;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'NormalizedApiError';
    this.code = error.code;
    this.status = error.status;
    this.retryable = error.retryable;
    if (error.correlationId !== undefined) this.correlationId = error.correlationId;
    if (error.retryAfterMs !== undefined) this.retryAfterMs = error.retryAfterMs;
  }
}

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? API_VERSION_PREFIX,
  timeout: 60_000,
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

let refreshPromise: Promise<string | null> | null = null;

export async function getValidAccessToken(): Promise<string | null> {
  let accessToken = getAccessToken();
  if (shouldRefreshBeforeRequest(undefined, accessToken)) {
    refreshPromise ??= refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
    accessToken = await refreshPromise;
  }
  return accessToken;
}

apiClient.interceptors.request.use(async (config) => {
  if (config.signal?.aborted) throw new axios.CanceledError('canceled', config);
  config.headers.set('X-Correlation-ID', crypto.randomUUID());
  config.headers.set('Accept-Language', document.documentElement.lang || 'en');
  let accessToken = getAccessToken();
  if (usesPortalQuota(config.url, config.helixAuth)) {
    const cooldown = apiRateLimitCooldown.remaining(rateLimitPartition(accessToken));
    if (cooldown > 0)
      throw new NormalizedApiError({
        code: 'http_429',
        status: 429,
        message: 'Too many requests. Please try again shortly.',
        retryable: true,
        retryAfterMs: cooldown,
      });
  }
  if (shouldRefreshBeforeRequest(config.url, accessToken)) {
    refreshPromise ??= refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
    accessToken = await refreshPromise;
  }
  if (accessToken !== null && config.helixAuth !== 'connector') {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return config;
});

function usesPortalQuota(url: string | undefined, auth: 'portal' | 'connector' | undefined) {
  if (auth === 'connector') return false;
  const path = new URL(url ?? '', window.location.origin).pathname;
  return !/(?:^|\/)auth(?:\/|$)/.test(path);
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (refreshToken === null) return null;
  try {
    const response = await axios.post<{
      isSuccess: boolean;
      data: { accessToken: string; refreshToken: string } | null;
    }>(`${apiClient.defaults.baseURL ?? API_VERSION_PREFIX}${apiRoutes.auth.refresh}`, {
      refreshToken,
    });
    if (!response.data.isSuccess || response.data.data === null) return null;
    setTokens(response.data.data);
    return response.data.data.accessToken;
  } catch {
    return null;
  }
}

function shouldRefreshBeforeRequest(url: string | undefined, accessToken: string | null): boolean {
  if (accessToken === null || url?.endsWith(apiRoutes.auth.refresh) === true) return false;
  const expiresAt = getJwtExpiryTime(accessToken);
  if (expiresAt === null) return false;
  return expiresAt - Date.now() <= 30_000;
}

function getJwtExpiryTime(token: string): number | null {
  const [, payload] = token.split('.');
  if (payload === undefined) return null;
  try {
    const normalized = payload.replaceAll('-', '+').replaceAll('_', '/');
    const json = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='))) as {
      exp?: unknown;
    };
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (axios.isCancel(error)) throw error;
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? 0;
      const original = error.config as
        (typeof error.config & { _helixRetried?: boolean }) | undefined;
      if (
        status === 401 &&
        original !== undefined &&
        original._helixRetried !== true &&
        original.helixAuth !== 'connector' &&
        !original.url?.endsWith(apiRoutes.auth.refresh)
      ) {
        original._helixRetried = true;
        refreshPromise ??= refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
        const accessToken = await refreshPromise;
        if (accessToken !== null) {
          original.headers.set('Authorization', `Bearer ${accessToken}`);
          return apiClient.request(original);
        }
      }
      const correlationId = error.response?.headers['x-correlation-id'] as string | undefined;
      const authorization = original?.headers.get('Authorization');
      const responseToken =
        typeof authorization === 'string' && authorization.startsWith('Bearer ')
          ? authorization.slice(7)
          : getAccessToken();
      const retryAfterMs =
        status === 429 && usesPortalQuota(original?.url, original?.helixAuth)
          ? apiRateLimitCooldown.record(
              rateLimitPartition(responseToken),
              error.response?.headers['retry-after'],
            )
          : undefined;
      const responseBody = error.response?.data as
        { message?: unknown; detail?: unknown; errorCode?: unknown } | undefined;
      const serverMessage =
        typeof responseBody?.message === 'string'
          ? responseBody.message
          : typeof responseBody?.detail === 'string'
            ? responseBody.detail
            : error.message;
      const normalized: ApiError = {
        code:
          typeof responseBody?.errorCode === 'string'
            ? responseBody.errorCode
            : status === 0
              ? 'network_error'
              : `http_${String(status)}`,
        message: serverMessage,
        status,
        retryable: status === 0 || status === 429 || status >= 500,
        ...(retryAfterMs === undefined ? {} : { retryAfterMs }),
        ...(correlationId === undefined ? {} : { correlationId }),
      };
      if (status === 401 && original?.helixAuth !== 'connector') {
        clearTokens();
        window.dispatchEvent(new CustomEvent('helix:session-expired'));
      }
      throw new NormalizedApiError(normalized);
    }
    throw error instanceof Error ? error : new Error('Unknown API error');
  },
);
